import { __decorate } from "tslib";
import React, { Children, cloneElement, createElement, isValidElement, } from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { action as mobxAction, computed, isArrayLike, observable, runInAction, toJS } from 'mobx';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import noop from 'lodash/noop';
import defaultTo from 'lodash/defaultTo';
import raf from 'raf';
import Responsive, { hasBreakPointMap, isBreakPointMap } from 'choerodon-ui/lib/responsive/Responsive';
import { pxToRem } from 'choerodon-ui/lib/_util/UnitConvertor';
import isFunction from 'lodash/isFunction';
import axios from '../axios';
import autobind from '../_util/autobind';
import isFragment from '../_util/isFragment';
import { getFieldsById } from '../field/FormField';
import FormContext, { FormProvider } from './FormContext';
import DataSetComponent from '../data-set/DataSetComponent';
import { FormLayout, LabelAlign, LabelLayout, SpacingType } from './enum';
import { defaultColumns, defaultExcludeUseColonTag, defaultLabelWidth, FIELD_SUFFIX, getProperty, getPropertyDSFirst, getSpacingFieldStyle, getSpacingLabelStyle, getSpacingProperties, hasParentElement, normalizeLabelWidth, normalizeSeparateSpacing, normalizeSpacingType, } from './utils';
import FormVirtualGroup from './FormVirtualGroup';
import { DataSetEvents } from '../data-set/enum';
import Item from './Item';
import FormItemLabel from './FormItemLabel';
import ItemGroup from './ItemGroup';
import { ShowHelp } from '../field/enum';
import Icon from '../icon';
import { hide, show } from '../tooltip/singleton';
/**
 * 表单name生成器
 */
const NameGen = (function* (start) {
    while (true) {
        start += 1;
        yield `form-${start}`;
    }
})(0);
let Form = class Form extends DataSetComponent {
    constructor(props, context) {
        super(props, context);
        this.fields = [];
        this.name = NameGen.next().value;
        this.validating = false;
        this.prepareForReport = {};
        runInAction(() => {
            this.responsiveItems = [];
        });
    }
    static get contextType() {
        return FormContext;
    }
    get axios() {
        return this.observableProps.axios || this.getContextConfig('axios') || axios;
    }
    get dataSet() {
        const { record } = this;
        if (record) {
            return record.dataSet;
        }
        const { observableProps } = this;
        if ('dataSet' in observableProps) {
            return observableProps.dataSet;
        }
        return observableProps.contextDataSet;
    }
    get record() {
        const { observableProps } = this;
        if ('record' in observableProps) {
            return observableProps.record;
        }
        const { dataSet, dataIndex } = observableProps;
        if (dataSet) {
            if (isNumber(dataIndex)) {
                return dataSet.get(dataIndex);
            }
            return dataSet.current;
        }
        if (isNumber(dataIndex)) {
            const { contextDataSet } = observableProps;
            if (contextDataSet) {
                return contextDataSet.get(dataIndex);
            }
        }
        return observableProps.contextRecord;
    }
    get dataIndex() {
        const { dataIndex, contextDataIndex } = this.observableProps;
        return defaultTo(dataIndex, contextDataIndex);
    }
    get useColon() {
        const { useColon } = this.observableProps;
        if (useColon !== undefined) {
            return useColon;
        }
        const configUseColon = this.getContextConfig('useColon');
        if (configUseColon !== undefined) {
            return configUseColon;
        }
        return false;
    }
    get excludeUseColonTagList() {
        const { excludeUseColonTagList } = this.observableProps;
        if (excludeUseColonTagList !== undefined) {
            return excludeUseColonTagList;
        }
        const configExcludeUseColonTagList = this.getContextConfig('excludeUseColonTagList');
        if (configExcludeUseColonTagList !== undefined) {
            return configExcludeUseColonTagList;
        }
        return defaultExcludeUseColonTag;
    }
    get columns() {
        const { columns } = this.observableProps;
        if (isNumber(columns)) {
            return columns;
        }
        if (columns) {
            const responsiveColumns = this.responsiveItems[0];
            if (responsiveColumns) {
                return responsiveColumns;
            }
        }
        return defaultColumns;
    }
    get labelWidth() {
        const { labelWidth } = this.observableProps;
        if (labelWidth === 'auto') {
            return labelWidth;
        }
        if (isNumber(labelWidth) || (isArrayLike(labelWidth) && !hasBreakPointMap(labelWidth))) {
            return labelWidth;
        }
        if (labelWidth) {
            const responsiveWidth = this.responsiveItems[1];
            if (responsiveWidth !== undefined) {
                return responsiveWidth;
            }
        }
        return defaultLabelWidth;
    }
    get labelAlign() {
        const { labelAlign } = this.observableProps;
        const defaultLabelAlign = this.labelLayout === LabelLayout.vertical ? LabelAlign.left : LabelAlign.right;
        if (isString(labelAlign)) {
            return labelAlign;
        }
        if (labelAlign) {
            const responsiveAlign = this.responsiveItems[2];
            if (responsiveAlign) {
                return responsiveAlign;
            }
        }
        return defaultLabelAlign;
    }
    get labelLayout() {
        const { labelLayout } = this.observableProps;
        if (isString(labelLayout)) {
            return labelLayout;
        }
        if (labelLayout) {
            const responsiveLabelLayout = this.responsiveItems[3];
            if (responsiveLabelLayout) {
                return responsiveLabelLayout;
            }
        }
        return this.getContextConfig('labelLayout') || LabelLayout.horizontal;
    }
    get labelTooltip() {
        const { labelTooltip } = this.observableProps;
        if (labelTooltip) {
            return labelTooltip;
        }
        const { getTooltip } = this.context;
        return getTooltip('label');
    }
    get readOnly() {
        return this.observableProps.readOnly;
    }
    get pristine() {
        return this.observableProps.pristine;
    }
    get fieldHighlightRenderer() {
        return this.observableProps.fieldHighlightRenderer;
    }
    get showValidation() {
        return this.observableProps.showValidation;
    }
    get showHelp() {
        const { showHelp } = this.observableProps;
        if (isString(showHelp)) {
            return showHelp;
        }
        return this.getContextConfig('showHelp') || ShowHelp.newLine;
    }
    get separateSpacing() {
        const { separateSpacing } = this.observableProps;
        if (separateSpacing) {
            const { width = 0, height = 0 } = normalizeSeparateSpacing(separateSpacing);
            if (width || height) {
                return {
                    width,
                    height,
                };
            }
            if (isBreakPointMap(separateSpacing)) {
                const responsiveSeparateSpacing = this.responsiveItems[4];
                if (responsiveSeparateSpacing) {
                    const { width = 0, height = 0 } = responsiveSeparateSpacing;
                    return {
                        width,
                        height,
                    };
                }
            }
        }
        return undefined;
    }
    get spacingType() {
        const { spacingType } = this.props;
        return normalizeSpacingType(spacingType);
    }
    isReadOnly() {
        return this.readOnly;
    }
    isDisabled() {
        const disabled = super.isDisabled();
        if (disabled) {
            return disabled;
        }
        const { record } = this;
        if (record) {
            return record.disabled;
        }
        return false;
    }
    getObservableProps(props, context) {
        const observableProps = {
            ...super.getObservableProps(props, context),
            contextDataSet: context.dataSet,
            contextRecord: context.record,
            dataIndex: props.dataIndex,
            contextDataIndex: context.dataIndex,
            labelLayout: 'labelLayout' in props ? props.labelLayout : context.labelLayout,
            labelAlign: 'labelAlign' in props ? props.labelAlign : context.labelAlign,
            showValidation: 'showValidation' in props ? props.showValidation : context.showValidation,
            showHelp: 'showHelp' in props ? props.showHelp : context.showHelp,
            labelTooltip: 'labelTooltip' in props ? props.labelTooltip : context.labelTooltip,
            disabled: 'disabled' in props ? props.disabled : context.disabled,
            readOnly: 'readOnly' in props ? props.readOnly : context.readOnly,
            labelWidth: defaultTo(props.labelWidth, context.labelWidth),
            pristine: 'pristine' in props ? props.pristine : context.pristine,
            columns: props.columns,
            useColon: props.useColon,
            excludeUseColonTagList: props.excludeUseColonTagList,
            separateSpacing: props.separateSpacing,
            fieldHighlightRenderer: 'fieldHighlightRenderer' in props ? props.fieldHighlightRenderer : context.fieldHighlightRenderer,
        };
        if ('record' in props) {
            observableProps.record = props.record;
        }
        return observableProps;
    }
    getOmitPropsKeys() {
        return super.getOmitPropsKeys().concat([
            'record',
            'dataIndex',
            'onSuccess',
            'onError',
            'processParams',
            'labelWidth',
            'labelAlign',
            'labelLayout',
            'labelTooltip',
            'columns',
            'pristine',
            'axios',
            'useColon',
            'excludeUseColonTagList',
            'separateSpacing',
            'spacingType',
            'fieldHighlightRenderer',
            'layout',
            'showValidation',
            'showHelp',
        ]);
    }
    getOtherProps() {
        const otherProps = super.getOtherProps();
        otherProps.onSubmit = this.handleSubmit;
        otherProps.onReset = this.handleReset;
        if (!otherProps.name) {
            otherProps.name = this.name;
        }
        return otherProps;
    }
    getHeader() {
        const { props: { header }, prefixCls, } = this;
        if (header) {
            return (React.createElement("div", { key: "form-header", className: `${prefixCls}-header` }, header));
        }
    }
    getClassName(...props) {
        const { prefixCls, labelLayout } = this;
        return super.getClassName({
            ...props,
            [`${prefixCls}-float-label`]: labelLayout === LabelLayout.float,
        });
    }
    componentWillMount() {
        this.processDataSetListener(true);
    }
    componentDidMount() {
        this.componentDidMountOrUpdate();
        this.handleFormFocus();
        super.componentDidMount();
    }
    componentDidUpdate() {
        this.componentDidMountOrUpdate();
    }
    componentWillUnmount() {
        this.processDataSetListener(false);
        if (this.isTooltipShown) {
            hide();
            delete this.isTooltipShown;
        }
        this.bubbleValidationReport(false);
    }
    handleFormFocus() {
        // 聚焦到form内第一个可编辑组件上
        const fields = this.getFields();
        let editableField;
        for (let i = 0; i < fields.length; i++) {
            const field = fields[i];
            if (field.props.autoFocus) {
                break;
            }
            if (field.editable && !editableField) {
                editableField = field;
            }
            if (i === fields.length - 1 && editableField && this.getContextConfig('formAutoFocus')) {
                editableField.focus();
            }
        }
    }
    componentDidMountOrUpdate() {
        const { formNode } = this.context;
        const { element } = this;
        if (formNode && element) {
            const isUnderForm = hasParentElement(element.parentElement, 'form');
            if (isUnderForm !== this.isUnderForm) {
                this.isUnderForm = isUnderForm;
            }
        }
    }
    processDataSetListener(flag) {
        const { dataSet } = this;
        if (dataSet) {
            const handler = flag ? dataSet.addEventListener : dataSet.removeEventListener;
            handler.call(dataSet, DataSetEvents.validate, this.handleDataSetValidate);
            handler.call(dataSet, DataSetEvents.reset, this.handleDataSetReset);
            handler.call(dataSet, DataSetEvents.remove, this.handleDataSetRemove);
        }
    }
    bubbleValidationReport(showInvalid) {
        const { pristine } = this.props;
        if (!pristine) {
            const onComponentValidationReport = this.getContextConfig('onComponentValidationReport');
            if (onComponentValidationReport) {
                onComponentValidationReport({
                    showInvalid,
                    component: this,
                });
            }
        }
    }
    handleDataSetReset({ record, dataSet }) {
        if (record) {
            const errors = dataSet.getAllValidationErrors();
            this.bubbleValidationReport(errors.dataSet.length > 0 || errors.records.length > 0);
        }
        else {
            this.bubbleValidationReport(false);
        }
    }
    handleDataSetRemove({ records, dataSet }) {
        if (records) {
            const errors = dataSet.getAllValidationErrors();
            this.bubbleValidationReport(errors.dataSet.length > 0 || errors.records.length > 0);
        }
    }
    // 处理校验失败定位
    handleDataSetValidate(props) {
        const { valid, errors: validationErrors, noLocate } = props;
        this.bubbleValidationReport(!valid);
        const { autoValidationLocate } = this.props;
        if (autoValidationLocate !== false && !noLocate && !valid) {
            const [firstInvalidRecord] = validationErrors;
            if (firstInvalidRecord) {
                const { errors } = firstInvalidRecord;
                if (errors.length) {
                    const [{ field: { name } }] = errors;
                    const field = this.getFields().find(item => item.props.name === name);
                    if (field) {
                        raf(() => {
                            field.focus();
                        });
                    }
                }
            }
        }
    }
    handleHelpMouseEnter(e, help) {
        const { target } = e;
        const { getTooltipTheme, getTooltipPlacement } = this.context;
        show(target, {
            title: help,
            theme: getTooltipTheme('help'),
            placement: getTooltipPlacement('help'),
        });
        this.isTooltipShown = true;
    }
    handleHelpMouseLeave() {
        hide();
    }
    renderTooltipHelp(help) {
        if (help) {
            return (React.createElement(Icon, { type: "help", onMouseEnter: (e) => this.handleHelpMouseEnter(e, help), onMouseLeave: this.handleHelpMouseLeave }));
        }
    }
    rasterizedChildren() {
        const { dataSet, record, columns, labelLayout, labelAlign, labelTooltip, useColon, excludeUseColonTagList, readOnly: formReadOnly, showHelp, props: { children }, } = this;
        const prefixCls = this.getContextProPrefixCls(FIELD_SUFFIX);
        const labelWidth = normalizeLabelWidth(this.labelWidth, columns);
        const rows = [];
        let cols = [];
        let rowIndex = 0;
        let colIndex = 0;
        const matrix = [[]];
        let noLabel = true;
        const childrenArray = [];
        const { separateSpacing } = this;
        const isLabelLayoutHorizontal = labelLayout === LabelLayout.horizontal;
        const spacingProperties = separateSpacing ? getSpacingProperties(separateSpacing, this.spacingType, isLabelLayoutHorizontal) : undefined;
        let isAllOutputCom = true;
        Children.forEach(children, (child) => {
            if (isValidElement(child)) {
                const setChild = (arr, outChild, groupProps = {}) => {
                    const { type, props: outChildProps } = outChild;
                    if (outChildProps.hidden)
                        return null;
                    if (type) {
                        if (isFunction(type) && !type.__PRO_OUTPUT &&
                            !(type.displayName === 'IntlField' && outChildProps && outChildProps.displayOutput)) {
                            isAllOutputCom = false;
                        }
                        if (noLabel === true &&
                            isLabelLayoutHorizontal &&
                            getProperty(outChildProps, 'label', dataSet, record)) {
                            noLabel = false;
                        }
                        const { children: outChildChildren, ...otherOutChildProps } = outChildProps;
                        const isFragmentElement = isFragment(outChild);
                        if (isFragmentElement || type.__PRO_FORM_VIRTUAL_GROUP) {
                            Children.forEach(outChildChildren, (c) => {
                                if (isValidElement(c)) {
                                    setChild(arr, c, isFragmentElement ? groupProps : { ...groupProps, ...otherOutChildProps });
                                }
                            });
                        }
                        else {
                            arr.push(cloneElement(outChild, {
                                ...groupProps,
                                ...outChildProps,
                            }));
                        }
                    }
                };
                setChild(childrenArray, child);
            }
        });
        function completeLine() {
            if (cols.length) {
                const maxRowSpan = Math.max(...cols.map(x => x.props.rowSpan));
                if (rows[rowIndex]) {
                    rows[rowIndex] = React.createElement("tr", { key: `row-${rowIndex}` }, cols);
                }
                else {
                    rows.push((React.createElement("tr", { key: `row-${rowIndex}` }, cols)));
                }
                if (maxRowSpan > 1) {
                    new Array(maxRowSpan - 1).fill(0).forEach(() => rows.push(React.createElement("tr", null)));
                }
                cols = [];
            }
            rowIndex++;
            colIndex = 0;
            matrix[rowIndex] = matrix[rowIndex] || [];
        }
        for (let index = 0, len = childrenArray.length; index < len;) {
            const { props, key, type, ref } = childrenArray[index];
            const TagName = isFunction(type) ? type.displayName : type;
            const label = getProperty(props, 'label', dataSet, record);
            const help = getProperty(props, 'help', dataSet, record);
            const fieldLabelWidth = getProperty(props, 'labelWidth', dataSet, record);
            const required = getPropertyDSFirst(props, 'required', dataSet, record);
            const readOnly = getProperty(props, 'readOnly', dataSet, record) || formReadOnly;
            const intlFieldOutput = TagName === 'IntlField' && props && props.displayOutput;
            const { rowSpan = 1, colSpan = 1, newLine, className, fieldClassName, useColon: fieldUseColon = useColon, ...otherProps } = props;
            let newColSpan = colSpan;
            const currentRow = matrix[rowIndex];
            if (newLine) {
                if (colIndex !== 0) {
                    completeLine();
                    continue;
                }
            }
            while (currentRow[colIndex]) {
                colIndex++;
            }
            if (colIndex >= columns) {
                completeLine();
                continue;
            }
            if (newColSpan + colIndex > columns) {
                newColSpan = columns - colIndex;
            }
            for (let i = colIndex, k = colIndex + newColSpan; i < k; i++) {
                if (currentRow[i]) {
                    newColSpan = i - colIndex;
                    break;
                }
            }
            for (let i = rowIndex; i < rowSpan + rowIndex; i++) {
                for (let j = colIndex, k = newColSpan + colIndex; j < k; j++) {
                    if (!matrix[i]) {
                        matrix[i] = [];
                    }
                    matrix[i][j] = true;
                }
            }
            const fieldElementProps = {
                ref,
                key,
                className: classNames(prefixCls, className),
                ...otherProps,
            };
            const isOutput = type.__PRO_OUTPUT || intlFieldOutput;
            const outputMix = !isAllOutputCom && isOutput ? 'mix' : '';
            const isLabelShowHelp = (fieldElementProps.showHelp || showHelp) === ShowHelp.label;
            const labelClassName = classNames(`${prefixCls}-label`, `${prefixCls}-label-${labelAlign}`, fieldClassName, {
                [`${prefixCls}-required`]: required && !isOutput,
                [`${prefixCls}-readonly`]: readOnly,
                [`${prefixCls}-label-vertical`]: labelLayout === LabelLayout.vertical,
                [`${prefixCls}-label-output`]: isLabelLayoutHorizontal && isOutput,
                [`${prefixCls}-label-output-${outputMix}`]: isLabelLayoutHorizontal && isOutput && outputMix,
                [`${prefixCls}-label-useColon`]: label && fieldUseColon && !excludeUseColonTagList.find(v => v === TagName),
                [`${prefixCls}-label-help`]: isLabelShowHelp,
            });
            const wrapperClassName = classNames(`${prefixCls}-wrapper`, {
                [`${prefixCls}-output`]: isLabelLayoutHorizontal && isOutput,
                [`${prefixCls}-output-${outputMix}`]: isLabelLayoutHorizontal && isOutput && outputMix,
            });
            if (!noLabel && !type.__PRO_FORM_ITEM) {
                const columnLabelWidth = labelWidth[colIndex];
                if (!isNaN(fieldLabelWidth) && columnLabelWidth !== 'auto') {
                    labelWidth[colIndex] = Math.max(columnLabelWidth, fieldLabelWidth);
                }
                const tooltip = 'labelTooltip' in props ? props.labelTooltip : labelTooltip;
                cols.push(React.createElement(FormItemLabel, { key: `row-${rowIndex}-col-${colIndex}-label`, className: labelClassName, rowSpan: rowSpan, style: spacingProperties ? getSpacingLabelStyle(spacingProperties, isLabelLayoutHorizontal, rowIndex) : undefined, tooltip: tooltip, help: isLabelShowHelp ? this.renderTooltipHelp(help) : undefined }, toJS(label)));
            }
            if (!isString(type)) {
                fieldElementProps.rowIndex = rowIndex;
                fieldElementProps.colIndex = colIndex;
            }
            const currentColSpan = noLabel ? newColSpan : newColSpan * 2 - (type.__PRO_FORM_ITEM ? 0 : 1);
            cols.push(React.createElement("td", { key: `row-${rowIndex}-col-${colIndex}-field`, colSpan: currentColSpan, rowSpan: rowSpan, className: fieldClassName, style: spacingProperties ? getSpacingFieldStyle(spacingProperties, isLabelLayoutHorizontal, rowIndex) : undefined },
                labelLayout === LabelLayout.vertical && !!label && (React.createElement(React.Fragment, null,
                    React.createElement("label", { className: labelClassName }, toJS(label)),
                    isLabelShowHelp ? this.renderTooltipHelp(help) : null)),
                React.createElement("div", { className: wrapperClassName }, createElement(type, fieldElementProps))));
            if (index === len - 1) {
                completeLine();
            }
            else {
                colIndex++;
            }
            index++;
        }
        cols = [];
        const isAutoWidth = !noLabel && labelWidth.some(w => w === 'auto');
        if (!noLabel) {
            for (let i = 0; i < columns; i++) {
                const key = `label-${i}`;
                const columnLabelWidth = labelWidth[i % columns];
                if (columnLabelWidth === 'auto') {
                    cols.push(React.createElement("col", { key: key }));
                }
                else {
                    cols.push(React.createElement("col", { key: key, 
                        // 优化当使用 separateSpacing label 宽度太窄问题
                        style: { width: pxToRem(spacingProperties ? spacingProperties.paddingHorizontal + columnLabelWidth : columnLabelWidth) } }));
                }
                cols.push(React.createElement("col", { key: `wrapper-${i}`, style: isAutoWidth ? { width: `${100 / columns}%` } : undefined }));
            }
        }
        else {
            for (let i = 0; i < columns; i++) {
                cols.push(React.createElement("col", { key: `wrapper-${i}` }));
            }
        }
        return (React.createElement("table", { key: "form-body", style: spacingProperties && spacingProperties.style, className: `${isAutoWidth ? 'auto-width' : ''}` },
            cols.length ? React.createElement("colgroup", null, cols) : undefined,
            React.createElement("tbody", null, rows)));
    }
    getMergedProps(props = {}) {
        const mergedProps = super.getMergedProps(props);
        if (this.spacingType.width === SpacingType.between && this.separateSpacing && this.separateSpacing.width > 0) {
            mergedProps.style = {
                ...mergedProps.style,
                overflowX: 'hidden',
            };
        }
        return mergedProps;
    }
    render() {
        const { labelWidth, labelAlign, labelLayout, labelTooltip, pristine, dataSet, record, dataIndex, observableProps, disabled, readOnly, fieldHighlightRenderer, props, useColon, showValidation, showHelp, } = this;
        const { formNode, getConfig, getProPrefixCls, getPrefixCls, getCustomizable, getTooltip, getTooltipTheme, getTooltipPlacement } = this.context;
        const value = {
            formNode: formNode || this,
            dataSet,
            dataIndex,
            record,
            labelWidth,
            labelAlign,
            labelLayout,
            labelTooltip,
            pristine,
            disabled,
            readOnly,
            fieldHighlightRenderer,
            useColon,
            showValidation,
            showHelp,
            getConfig,
            getPrefixCls,
            getProPrefixCls,
            getCustomizable,
            getTooltip,
            getTooltipTheme,
            getTooltipPlacement,
        };
        const children = [
            this.getHeader(),
            props.layout === FormLayout.table ? this.rasterizedChildren() : props.children,
        ];
        return (React.createElement(Responsive, { items: [
                observableProps.columns,
                observableProps.labelWidth,
                observableProps.labelAlign,
                observableProps.labelLayout,
                observableProps.separateSpacing,
            ], onChange: this.handleResponsive },
            React.createElement(FormProvider, { value: value }, this.isUnderForm ? children :
                formNode && this.isUnderForm === undefined ? (React.createElement("div", Object.assign({}, this.getMergedProps()), children)) : (React.createElement("form", Object.assign({}, this.getMergedProps(), { noValidate: true }), children)))));
    }
    handleResponsive(items) {
        this.responsiveItems = items;
    }
    async handleSubmit(e) {
        e.preventDefault();
        e.persist();
        if (await this.checkValidity()) {
            const { target, action, dataSet, method, processParams = noop, onSuccess = noop, onError = noop, onSubmit = noop, } = this.props;
            onSubmit(e);
            try {
                if (dataSet) {
                    onSuccess(await dataSet.submit());
                }
                else if (action) {
                    if (target && this.element) {
                        this.element.submit();
                    }
                    else {
                        onSuccess(await this.axios[method || 'get'](action, processParams(e)));
                    }
                }
            }
            catch (error) {
                onError(error);
            }
        }
    }
    handleReset(e) {
        const { onReset = noop } = this.props;
        const { record } = this;
        onReset(e);
        if (!e.isDefaultPrevented()) {
            if (record) {
                record.reset();
            }
            else {
                this.getFields().forEach(field => field.reset());
            }
        }
    }
    reportValidity(result) {
        const { prepareForReport } = this;
        if (!result) {
            prepareForReport.result = result;
        }
        if (prepareForReport.timeout) {
            window.clearTimeout(prepareForReport.timeout);
        }
        prepareForReport.timeout = window.setTimeout(() => {
            if (!prepareForReport.result) {
                const field = this.getFields().find(one => !one.valid);
                if (field) {
                    field.focus();
                }
            }
            this.prepareForReport = {};
        }, 200);
    }
    checkValidity() {
        const { dataSet } = this;
        if (dataSet) {
            if (!dataSet.length) {
                dataSet.create();
            }
            return dataSet.validate();
        }
        this.validating = true;
        return Promise.all(this.getFields().map(field => field.checkValidity())).then((results) => {
            const valid = results.every(result => result);
            this.reportValidity(valid);
            this.validating = false;
            return valid;
        }).catch((error) => {
            this.validating = false;
            throw error;
        });
    }
    getFields() {
        const { id } = this.props;
        if (id) {
            return [].concat(this.fields, getFieldsById(id));
        }
        return this.fields;
    }
    getField(name) {
        return this.getFields().find(field => field.props.name === name);
    }
    addField(field) {
        this.fields.push(field);
    }
    removeField(field) {
        const index = this.fields.indexOf(field);
        if (index !== -1) {
            this.fields.splice(index, 1);
        }
    }
};
Form.displayName = 'Form';
Form.ItemGroup = ItemGroup;
Form.FormVirtualGroup = FormVirtualGroup;
Form.Item = Item;
Form.defaultProps = {
    suffixCls: 'form',
    columns: defaultColumns,
    labelWidth: defaultLabelWidth,
    layout: FormLayout.table,
};
__decorate([
    observable
], Form.prototype, "responsiveItems", void 0);
__decorate([
    observable
], Form.prototype, "isUnderForm", void 0);
__decorate([
    computed
], Form.prototype, "axios", null);
__decorate([
    computed
], Form.prototype, "dataSet", null);
__decorate([
    computed
], Form.prototype, "record", null);
__decorate([
    computed
], Form.prototype, "dataIndex", null);
__decorate([
    computed
], Form.prototype, "useColon", null);
__decorate([
    computed
], Form.prototype, "excludeUseColonTagList", null);
__decorate([
    computed
], Form.prototype, "columns", null);
__decorate([
    computed
], Form.prototype, "labelWidth", null);
__decorate([
    computed
], Form.prototype, "labelAlign", null);
__decorate([
    computed
], Form.prototype, "labelLayout", null);
__decorate([
    computed
], Form.prototype, "labelTooltip", null);
__decorate([
    computed
], Form.prototype, "readOnly", null);
__decorate([
    computed
], Form.prototype, "pristine", null);
__decorate([
    computed
], Form.prototype, "fieldHighlightRenderer", null);
__decorate([
    computed
], Form.prototype, "showValidation", null);
__decorate([
    computed
], Form.prototype, "showHelp", null);
__decorate([
    computed
], Form.prototype, "separateSpacing", null);
__decorate([
    mobxAction
], Form.prototype, "componentDidMountOrUpdate", null);
__decorate([
    autobind
], Form.prototype, "handleDataSetReset", null);
__decorate([
    autobind
], Form.prototype, "handleDataSetRemove", null);
__decorate([
    autobind
], Form.prototype, "handleDataSetValidate", null);
__decorate([
    autobind
], Form.prototype, "handleHelpMouseEnter", null);
__decorate([
    autobind
], Form.prototype, "handleHelpMouseLeave", null);
__decorate([
    autobind,
    mobxAction
], Form.prototype, "handleResponsive", null);
__decorate([
    autobind
], Form.prototype, "handleSubmit", null);
__decorate([
    autobind
], Form.prototype, "handleReset", null);
Form = __decorate([
    observer
], Form);
export default Form;
